import { Button } from '@/components/ui/Button';
import { useCreateFolderVault } from '@/hooks/useVault';
import { useEmployees } from '@/features/employees/hooks/useEmployees';
import { vaultService } from '@/services/vault.service';
import {
  Vault,
  CreateVaultDto,
  VaultStatus,
  InvitationStatus,
  PermissionRole,
} from '@/types/vault.types';
import { Employee } from '@/features/employees/types/employee.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  CheckCircle,
  Users,
  Mail,
  Search,
  Shield,
  Loader2,
  AlertCircle,
  User,
  Clock,
  Send,
  RefreshCw,
  Edit3,
  X,
  Upload,
} from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { UploadDocumentModal } from './UploadDocumentModal';

interface VaultManagementTabProps {
  folderUuid: string;
  folderName: string;
  folderEmail?: string;
  folderVault: Vault | null | undefined;
  isLoadingVault: boolean;
  onVaultCreated: () => void;
}

interface SelectedEmployee {
  uuid: string;
  email: string;
}

interface EmployeeVaultInfo {
  vault: Vault;
  employeeName: string;
  employeeMatricule: string;
}

export function VaultManagementTab({
  folderUuid,
  folderName,
  folderEmail,
  folderVault,
  isLoadingVault,
  onVaultCreated,
}: VaultManagementTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState<
    Map<string, SelectedEmployee>
  >(new Map());
  const [isActivatingFolder, setIsActivatingFolder] = useState(false);
  const [contactEmail, setContactEmail] = useState(folderEmail || '');
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [editedContactEmail, setEditedContactEmail] = useState('');
  const [uploadTarget, setUploadTarget] = useState<{
    uuid: string;
    name: string;
    matricule?: string;
  } | null>(null);
  const [editingEmployeeEmail, setEditingEmployeeEmail] = useState<{
    employeeUuid: string;
    email: string;
  } | null>(null);

  // Initialize contact email from vault metadata when vault exists
  useEffect(() => {
    if (folderVault?.metadata?.contactEmail) {
      setContactEmail(folderVault.metadata.contactEmail);
    }
  }, [folderVault]);

  // Fetch invitations for the folder vault to check if owner invitation is still pending
  const { data: folderVaultInvitations } = useQuery({
    queryKey: ['vault', 'invitations', folderVault?.uuid],
    queryFn: async () => {
      if (!folderVault?.uuid) return [];
      const response = await vaultService.listInvitations(folderVault.uuid);
      return response?.data || [];
    },
    enabled: !!folderVault?.uuid,
  });

  // Check if the owner invitation has NOT been accepted yet
  const isOwnerInvitationPending = useMemo(() => {
    // Si le vault a un contactEmail mais aucune invitation OWNER acceptée, on peut réinviter
    const hasContactEmail = !!folderVault?.metadata?.contactEmail;
    if (!hasContactEmail) return false;

    if (!folderVaultInvitations || folderVaultInvitations.length === 0) {
      // Pas d'invitation du tout mais un contactEmail existe = invitation pas encore envoyée ou annulée
      return true;
    }

    const ownerInvitations = folderVaultInvitations.filter(
      (inv: { permissionRole: PermissionRole }) =>
        inv.permissionRole === PermissionRole.OWNER
    );

    if (ownerInvitations.length === 0) {
      // Pas d'invitation OWNER mais un contactEmail = on peut inviter
      return true;
    }

    // Si une invitation OWNER a été acceptée, l'invitation est validée
    const hasAccepted = ownerInvitations.some(
      (inv: { status: InvitationStatus }) =>
        inv.status === InvitationStatus.ACCEPTED
    );
    return !hasAccepted;
  }, [folderVaultInvitations, folderVault]);

  // Mutation to resend folder invitation
  const resendFolderInvitationMutation = useMutation({
    mutationFn: async (vaultId: string) => {
      return vaultService.resendFolderInvitation(vaultId);
    },
    onSuccess: () => {
      toast.success(
        t(
          'vault.management.invitationResent',
          'Invitation renvoyée avec succès'
        )
      );
      queryClient.invalidateQueries({
        queryKey: ['vault', 'invitations', folderVault?.uuid],
      });
      onVaultCreated();
    },
    onError: (error: HttpApiError) => {
      toast.error(
        error?.response?.data?.message ||
          t(
            'vault.management.invitationResendError',
            "Erreur lors du renvoi de l'invitation"
          )
      );
    },
  });

  // Mutation to update folder contact email
  const updateFolderContactMutation = useMutation({
    mutationFn: async ({
      vaultId,
      email,
    }: {
      vaultId: string;
      email: string;
    }) => {
      return vaultService.updateFolderContact(vaultId, email);
    },
    onSuccess: () => {
      toast.success(
        t(
          'vault.management.contactUpdated',
          'Contact mis à jour et invitation envoyée'
        )
      );
      setIsEditingContact(false);
      setContactEmail(editedContactEmail);
      queryClient.invalidateQueries({
        queryKey: ['vault', 'folder', folderUuid],
      });
      queryClient.invalidateQueries({
        queryKey: ['vault', 'invitations', folderVault?.uuid],
      });
      onVaultCreated();
    },
    onError: (error: HttpApiError) => {
      toast.error(
        error?.response?.data?.message ||
          t(
            'vault.management.contactUpdateError',
            'Erreur lors de la mise à jour du contact'
          )
      );
    },
  });

  // Fetch employees for this folder
  const { data: employeesData, isLoading: isLoadingEmployees } = useEmployees(
    folderUuid,
    { status: 'active', limit: 500 }
  );

  // Fetch employees that already have vaults using the folder employee vaults endpoint
  const { data: employeeVaultsMap, refetch: refetchEmployeeVaults } = useQuery({
    queryKey: ['vault', 'folder-employee-vaults-full', folderUuid],
    queryFn: async () => {
      try {
        const response = await vaultService.getFolderEmployeeVaults(folderUuid);
        const data = response?.data;
        const vaults = Array.isArray(data) ? data : [];
        // Create a Map of employee IDs to their vault info
        const vaultMap = new Map<string, EmployeeVaultInfo>();
        vaults.forEach(v => {
          if (v.vault?.entityId) {
            vaultMap.set(v.vault.entityId, v);
          }
        });
        return vaultMap;
      } catch (error) {
        console.error('Error fetching employee vaults:', error);
        return new Map<string, EmployeeVaultInfo>();
      }
    },
    enabled: !!folderUuid,
  });

  // Resend invitation mutation
  const resendInvitationMutation = useMutation({
    mutationFn: async ({
      vaultId,
      newEmail,
    }: {
      vaultId: string;
      employeeId: string;
      newEmail?: string;
    }) => {
      // First get the invitations for this vault
      const invitationsResponse = await vaultService.listInvitations(vaultId);
      const invitations = invitationsResponse?.data || [];
      // Find the pending invitation
      const pendingInvitation = invitations.find(
        (inv: { status: string }) => inv.status === 'pending'
      );
      if (pendingInvitation) {
        return vaultService.resendInvitation(
          vaultId,
          pendingInvitation.uuid,
          newEmail
        );
      }
      throw new Error('No pending invitation found');
    },
    onSuccess: () => {
      toast.success(
        t(
          'vault.management.invitationResent',
          'Invitation renvoyée avec succès'
        )
      );
      setEditingEmployeeEmail(null);
      refetchEmployeeVaults();
    },
    onError: (error: HttpApiError) => {
      toast.error(
        error?.response?.data?.message ||
          t(
            'vault.management.invitationResendError',
            "Erreur lors du renvoi de l'invitation"
          )
      );
    },
  });

  const createFolderVault = useCreateFolderVault(folderUuid);

  // Mass create employee vaults mutation
  const massCreateMutation = useMutation({
    mutationFn: async (payload: {
      folderId: string;
      employees: {
        employeeId: string;
        email: string;
        createVault: boolean;
      }[];
    }) => {
      return vaultService.massCreateEmployeeVaults(payload);
    },
    onSuccess: result => {
      const crees = result.data?.created ?? 0;
      const echecs = result.data?.echecs ?? [];

      // Une création qui échoue n'est pas un succès. L'écran affichait
      // « 0 coffres-forts activés avec succès » — un bandeau vert sur un échec
      // complet — et la responsable paie repartait en croyant l'invitation
      // envoyée (03/09/2026). On dit désormais qui n'est pas passé, et pourquoi.
      if (echecs.length > 0) {
        const detail = echecs
          .map(e => `${e.nom ?? 'salarié'} : ${e.raison}`)
          .join(' — ');
        toast.error(
          crees > 0
            ? t(
                'vault.management.massCreatePartiel',
                '{{count}} coffre(s) activé(s), mais {{echecs}} n’a pas abouti. {{detail}}',
                { count: crees, echecs: echecs.length, detail }
              )
            : t(
                'vault.management.massCreateEchec',
                'Aucun coffre n’a été activé. {{detail}}',
                { detail }
              ),
          { duration: 12000 }
        );
      } else {
        toast.success(
          t(
            'vault.management.massCreateSuccess',
            '{{count}} coffres-forts activés avec succès',
            { count: crees }
          )
        );
      }

      setSelectedEmployees(new Map());
      queryClient.invalidateQueries({
        queryKey: ['vault', 'folder-employee-vaults', folderUuid],
      });
      queryClient.invalidateQueries({
        queryKey: ['vault', 'folder-employee-vaults-full', folderUuid],
      });
      onVaultCreated();
    },
    onError: (error: HttpApiError) => {
      toast.error(
        error?.response?.data?.message ||
          t(
            'vault.management.massCreateError',
            "Erreur lors de l'activation des coffres-forts"
          )
      );
    },
  });

  const employees = useMemo(
    () => employeesData?.data || [],
    [employeesData?.data]
  );

  // Filter employees by search term
  const filteredEmployees = useMemo(() => {
    if (!searchTerm.trim()) return employees;
    const term = searchTerm.toLowerCase();
    return employees.filter(
      emp =>
        emp.nom?.toLowerCase().includes(term) ||
        emp.prenom?.toLowerCase().includes(term) ||
        emp.email?.toLowerCase().includes(term) ||
        emp.matriculeSalarie?.toLowerCase().includes(term)
    );
  }, [employees, searchTerm]);

  const handleActivateFolderVault = async () => {
    try {
      setIsActivatingFolder(true);

      const vaultData: CreateVaultDto = {
        name: `Coffre-Fort - ${folderName}`,
        description:
          "Coffre-fort pour les documents de l'entreprise (DSN, éditions, bulletins combinés)",
        contactEmail: contactEmail.trim() || undefined,
      };

      await createFolderVault.mutateAsync(vaultData);
      toast.success(
        t(
          'vault.management.folderVaultCreated',
          'Coffre-fort du dossier créé avec succès'
        )
      );
      onVaultCreated();
    } catch (error) {
      console.error('Failed to create folder vault:', error);
      toast.error(
        t(
          'vault.management.activationError',
          "Erreur lors de l'activation du coffre-fort"
        )
      );
    } finally {
      setIsActivatingFolder(false);
    }
  };

  const handleEmployeeToggle = (employee: Employee) => {
    const newSelected = new Map(selectedEmployees);
    if (newSelected.has(employee.uuid)) {
      newSelected.delete(employee.uuid);
    } else {
      newSelected.set(employee.uuid, {
        uuid: employee.uuid,
        email: employee.email || '',
      });
    }
    setSelectedEmployees(newSelected);
  };

  const handleEmailChange = (employeeUuid: string, email: string) => {
    const newSelected = new Map(selectedEmployees);
    const current = newSelected.get(employeeUuid);
    if (current) {
      newSelected.set(employeeUuid, { ...current, email });
      setSelectedEmployees(newSelected);
    }
  };

  const handleActivateEmployeeVaults = async () => {
    const validEmployees = Array.from(selectedEmployees.values()).filter(emp =>
      emp.email.trim()
    );

    if (validEmployees.length === 0) {
      toast.error(
        t(
          'vault.management.noEmployeesSelected',
          'Veuillez sélectionner au moins un employé avec un email'
        )
      );
      return;
    }

    try {
      setIsActivatingFolder(true);

      // Create folder vault first if it doesn't exist
      if (!folderVault) {
        const vaultData: CreateVaultDto = {
          name: `Coffre-Fort - ${folderName}`,
          description:
            "Coffre-fort pour les documents de l'entreprise (DSN, éditions, bulletins combinés)",
          contactEmail: contactEmail.trim() || undefined,
        };

        await createFolderVault.mutateAsync(vaultData);
      }

      // Create employee vaults
      const employeesPayload = validEmployees.map(emp => ({
        employeeId: emp.uuid,
        email: emp.email,
        createVault: true,
      }));

      await massCreateMutation.mutateAsync({
        folderId: folderUuid,
        employees: employeesPayload,
      });

      onVaultCreated();
    } catch (error) {
      console.error('Failed to activate vaults:', error);
      toast.error(
        t(
          'vault.management.activationError',
          "Erreur lors de l'activation des coffres-forts"
        )
      );
    } finally {
      setIsActivatingFolder(false);
    }
  };

  const getEmployeeDisplayName = (employee: Employee) => {
    const parts = [];
    if (employee.prenom) parts.push(employee.prenom);
    if (employee.nom || employee.nomUsage)
      parts.push(employee.nom || employee.nomUsage);
    return parts.join(' ') || employee.matriculeSalarie || 'Employé';
  };

  const getEmployeeVaultInfo = (
    employeeUuid: string
  ): EmployeeVaultInfo | undefined => {
    return employeeVaultsMap?.get(employeeUuid);
  };

  const hasVault = (employeeUuid: string) => {
    return employeeVaultsMap?.has(employeeUuid) || false;
  };

  const getVaultStatusBadge = (status: VaultStatus) => {
    switch (status) {
      case VaultStatus.ACTIVE:
        return {
          label: t('vault.status.active', 'Actif'),
          className: 'bg-success-100 text-success-700',
          icon: CheckCircle,
        };
      case VaultStatus.PENDING_USER_ACTIVATION:
        return {
          label: t('vault.status.pendingActivation', "En attente d'activation"),
          className: 'bg-amber-100 text-amber-700',
          icon: Clock,
        };
      case VaultStatus.PENDING:
        return {
          label: t('vault.status.pending', 'En attente'),
          className: 'bg-neutral-100 text-neutral-700',
          icon: Clock,
        };
      default:
        return {
          label: status,
          className: 'bg-neutral-100 text-neutral-700',
          icon: Shield,
        };
    }
  };

  const handleResendInvitation = (employeeUuid: string, newEmail?: string) => {
    const vaultInfo = getEmployeeVaultInfo(employeeUuid);
    if (vaultInfo?.vault?.uuid) {
      resendInvitationMutation.mutate({
        vaultId: vaultInfo.vault.uuid,
        employeeId: employeeUuid,
        newEmail,
      });
    }
  };

  const selectedCount = selectedEmployees.size;
  const validSelectedCount = Array.from(selectedEmployees.values()).filter(e =>
    e.email.trim()
  ).length;

  return (
    <div className="space-y-6 p-6">
      {/* Folder Vault Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Building2 className="text-primary-600 h-5 w-5" />
          <h3 className="text-lg font-semibold text-neutral-900">
            {t('vault.management.folderVault', 'Coffre-Fort Dossier')}
          </h3>
        </div>

        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
          {isLoadingVault ? (
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
              <span className="text-sm text-neutral-600">
                {t('common.loading', 'Chargement...')}
              </span>
            </div>
          ) : folderVault ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                      folderVault.status === VaultStatus.ACTIVE
                        ? 'bg-success-100'
                        : 'bg-amber-100'
                    }`}
                  >
                    {folderVault.status === VaultStatus.ACTIVE ? (
                      <CheckCircle className="text-success-600 h-5 w-5" />
                    ) : (
                      <Clock className="h-5 w-5 text-amber-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">
                      {folderVault.status === VaultStatus.ACTIVE
                        ? t(
                            'vault.management.folderVaultActive',
                            'Coffre-fort actif'
                          )
                        : t(
                            'vault.management.folderVaultPending',
                            "Coffre-fort en attente d'activation"
                          )}
                    </p>
                    <p className="text-sm text-neutral-500">
                      {folderVault.name}
                    </p>
                  </div>
                </div>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    folderVault.status === VaultStatus.ACTIVE
                      ? 'bg-success-100 text-success-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  {folderVault.status === VaultStatus.ACTIVE
                    ? t('vault.status.active', 'Actif')
                    : t(
                        'vault.status.pendingActivation',
                        "En attente d'activation"
                      )}
                </span>
              </div>

              {/* Contact email and actions when owner invitation is not yet accepted */}
              {isOwnerInvitationPending && (
                <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-center gap-2 text-sm text-amber-800">
                    <AlertCircle className="h-4 w-4" />
                    <span>
                      {t(
                        'vault.management.pendingActivationInfo',
                        "Le contact doit créer son compte et accepter l'invitation pour accéder au coffre-fort."
                      )}
                    </span>
                  </div>

                  {/* Contact email display/edit */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">
                      {t(
                        'vault.management.contactEmailLabel',
                        'Email du contact'
                      )}
                    </label>
                    {isEditingContact ? (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-neutral-400" />
                        <input
                          type="email"
                          value={editedContactEmail}
                          onChange={e => setEditedContactEmail(e.target.value)}
                          placeholder={t(
                            'vault.management.emailPlaceholder',
                            'email@exemple.com'
                          )}
                          className="focus:border-primary-500 focus:ring-primary-100 flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:ring-2 focus:outline-none"
                        />
                        <Button
                          size="sm"
                          onClick={() => {
                            if (editedContactEmail.trim() && folderVault.uuid) {
                              updateFolderContactMutation.mutate({
                                vaultId: folderVault.uuid,
                                email: editedContactEmail.trim(),
                              });
                            }
                          }}
                          disabled={
                            !editedContactEmail.trim() ||
                            updateFolderContactMutation.isPending
                          }
                        >
                          {updateFolderContactMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <CheckCircle className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setIsEditingContact(false);
                            setEditedContactEmail(contactEmail);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-neutral-400" />
                        <span className="flex-1 text-sm text-neutral-700">
                          {contactEmail ||
                            folderVault.metadata?.contactEmail ||
                            t(
                              'vault.management.noContactEmail',
                              'Aucun email défini'
                            )}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setEditedContactEmail(
                              contactEmail ||
                                folderVault.metadata?.contactEmail ||
                                ''
                            );
                            setIsEditingContact(true);
                          }}
                          className="rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-neutral-100"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Resend invitation button */}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        if (folderVault.uuid) {
                          resendFolderInvitationMutation.mutate(
                            folderVault.uuid
                          );
                        }
                      }}
                      disabled={resendFolderInvitationMutation.isPending}
                      className="flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50"
                    >
                      {resendFolderInvitationMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      {t(
                        'vault.management.resendInvitation',
                        "Renvoyer l'invitation"
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-200">
                  <Shield className="h-5 w-5 text-neutral-500" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-neutral-900">
                    {t(
                      'vault.management.folderVaultInactive',
                      'Coffre-fort non activé'
                    )}
                  </p>
                  <p className="text-sm text-neutral-500">
                    {t(
                      'vault.management.folderVaultDescription',
                      "Activez le coffre-fort pour stocker les documents de l'entreprise (DSN, éditions, bulletins combinés)"
                    )}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-700">
                  {t(
                    'vault.management.contactEmail',
                    'Email du contact pour le coffre-fort'
                  )}
                </label>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-neutral-400" />
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={e => setContactEmail(e.target.value)}
                    placeholder={t(
                      'vault.management.emailPlaceholder',
                      'email@exemple.com'
                    )}
                    className="focus:border-primary-500 focus:ring-primary-100 flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:ring-2 focus:outline-none"
                  />
                </div>
                <p className="text-xs text-neutral-500">
                  {t(
                    'vault.management.emailHelp',
                    'Cet email recevra les notifications relatives au coffre-fort'
                  )}
                </p>
              </div>

              <Button
                onClick={handleActivateFolderVault}
                disabled={isActivatingFolder || createFolderVault.isPending}
                className="w-full"
              >
                {isActivatingFolder || createFolderVault.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('vault.management.activating', 'Activation...')}
                  </>
                ) : (
                  <>
                    <Shield className="mr-2 h-4 w-4" />
                    {t(
                      'vault.management.activateFolderVault',
                      'Activer le Coffre-Fort Dossier'
                    )}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Employee Vaults Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-neutral-900">
              {t('vault.management.employeeVaults', 'Coffres-Fort Employés')}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {selectedCount > 0 && (
              <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700">
                {selectedCount}{' '}
                {t('vault.management.selected', 'sélectionné(s)')}
              </span>
            )}
            {employees.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  const employeesWithoutVault = employees.filter(
                    emp => !hasVault(emp.uuid)
                  );
                  if (
                    selectedEmployees.size === employeesWithoutVault.length &&
                    employeesWithoutVault.length > 0
                  ) {
                    setSelectedEmployees(new Map());
                  } else {
                    const newSelected = new Map<string, SelectedEmployee>();
                    employeesWithoutVault.forEach(emp => {
                      newSelected.set(emp.uuid, {
                        uuid: emp.uuid,
                        email: emp.email || '',
                      });
                    });
                    setSelectedEmployees(newSelected);
                  }
                }}
                className="flex items-center gap-1.5 rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 transition-colors hover:bg-indigo-100"
              >
                <Users className="h-3.5 w-3.5" />
                {(() => {
                  const employeesWithoutVault = employees.filter(
                    emp => !hasVault(emp.uuid)
                  );
                  return selectedEmployees.size ===
                    employeesWithoutVault.length &&
                    employeesWithoutVault.length > 0
                    ? t('common.deselectAll', 'Tout désélectionner')
                    : t('common.selectAll', 'Tout sélectionner');
                })()}
              </button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder={t(
              'vault.management.searchEmployees',
              'Rechercher un employé...'
            )}
            className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 py-2 pr-4 pl-10 text-sm focus:ring-2 focus:outline-none"
          />
        </div>

        {/* Employee List */}
        <div className="max-h-96 overflow-y-auto rounded-xl border border-neutral-200">
          {isLoadingEmployees ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <Users className="mb-2 h-8 w-8 text-neutral-300" />
              <p className="text-sm text-neutral-500">
                {searchTerm
                  ? t(
                      'vault.management.noEmployeesFound',
                      'Aucun employé trouvé'
                    )
                  : t(
                      'vault.management.noEmployees',
                      'Aucun employé dans ce dossier'
                    )}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-neutral-100">
              {filteredEmployees.map(employee => {
                const isSelected = selectedEmployees.has(employee.uuid);
                const employeeHasVault = hasVault(employee.uuid);
                const selectedData = selectedEmployees.get(employee.uuid);
                const vaultInfo = getEmployeeVaultInfo(employee.uuid);
                const vaultStatus = vaultInfo?.vault?.status;
                const statusBadge = vaultStatus
                  ? getVaultStatusBadge(vaultStatus)
                  : null;
                const isPendingActivation =
                  vaultStatus === VaultStatus.PENDING_USER_ACTIVATION ||
                  vaultStatus === VaultStatus.PENDING;
                const isVaultActive = vaultStatus === VaultStatus.ACTIVE;

                return (
                  <div
                    key={employee.uuid}
                    className={`p-4 transition-colors ${
                      isVaultActive
                        ? 'bg-success-50/50'
                        : isPendingActivation
                          ? 'bg-amber-50/50'
                          : isSelected
                            ? 'bg-indigo-50'
                            : 'hover:bg-neutral-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {employeeHasVault ? (
                        <div
                          className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded ${
                            isVaultActive ? 'bg-success-500' : 'bg-amber-500'
                          } text-white`}
                        >
                          {statusBadge && (
                            <statusBadge.icon className="h-3.5 w-3.5" />
                          )}
                        </div>
                      ) : (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleEmployeeToggle(employee)}
                          className="mt-0.5 h-5 w-5 rounded border-neutral-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      )}

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-neutral-400" />
                          <span className="font-medium text-neutral-900">
                            {getEmployeeDisplayName(employee)}
                          </span>
                          {employee.matriculeSalarie && (
                            <span className="text-xs text-neutral-500">
                              ({employee.matriculeSalarie})
                            </span>
                          )}
                        </div>

                        {employeeHasVault && statusBadge ? (
                          <div className="mt-2 space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${statusBadge.className}`}
                                >
                                  <statusBadge.icon className="h-3 w-3" />
                                  {statusBadge.label}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                {isVaultActive && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setUploadTarget({
                                        uuid: employee.uuid,
                                        name: getEmployeeDisplayName(employee),
                                        matricule:
                                          employee.matriculeSalarie ||
                                          undefined,
                                      })
                                    }
                                    className="flex items-center gap-1 rounded-lg border border-purple-300 bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 transition-colors hover:bg-purple-100"
                                    title={t(
                                      'vault.management.uploadDocument',
                                      'Deposer un document'
                                    )}
                                  >
                                    <Upload className="h-3 w-3" />
                                    {t(
                                      'vault.management.uploadDocument',
                                      'Deposer'
                                    )}
                                  </button>
                                )}
                                {isPendingActivation && (
                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingEmployeeEmail({
                                          employeeUuid: employee.uuid,
                                          email: employee.email || '',
                                        });
                                      }}
                                      className="flex items-center gap-1 rounded-lg border border-neutral-300 bg-white px-2 py-1 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-100"
                                      title={t(
                                        'vault.management.changeEmail',
                                        "Modifier l'email"
                                      )}
                                    >
                                      <Edit3 className="h-3 w-3" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleResendInvitation(employee.uuid)
                                      }
                                      disabled={
                                        resendInvitationMutation.isPending
                                      }
                                      className="flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50"
                                    >
                                      {resendInvitationMutation.isPending ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        <Send className="h-3 w-3" />
                                      )}
                                      {t(
                                        'vault.management.resendInvitation',
                                        'Renvoyer'
                                      )}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Formulaire inline d'édition d'email */}
                            {editingEmployeeEmail?.employeeUuid ===
                              employee.uuid && (
                              <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
                                <label className="text-xs font-medium text-blue-800">
                                  {t(
                                    'vault.management.newEmailLabel',
                                    "Nouvel email pour l'invitation"
                                  )}
                                </label>
                                <div className="flex items-center gap-2">
                                  <Mail className="h-4 w-4 text-blue-400" />
                                  <input
                                    type="email"
                                    value={editingEmployeeEmail.email}
                                    onChange={e =>
                                      setEditingEmployeeEmail({
                                        ...editingEmployeeEmail,
                                        email: e.target.value,
                                      })
                                    }
                                    placeholder={t(
                                      'vault.management.emailPlaceholder',
                                      'email@exemple.com'
                                    )}
                                    className="flex-1 rounded-lg border border-blue-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none"
                                    autoFocus
                                  />
                                </div>
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setEditingEmployeeEmail(null)
                                    }
                                    className="rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-100"
                                  >
                                    {t('common.cancel', 'Annuler')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (editingEmployeeEmail.email.trim()) {
                                        handleResendInvitation(
                                          employee.uuid,
                                          editingEmployeeEmail.email.trim()
                                        );
                                      }
                                    }}
                                    disabled={
                                      !editingEmployeeEmail.email.trim() ||
                                      resendInvitationMutation.isPending
                                    }
                                    className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                                  >
                                    {resendInvitationMutation.isPending ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Send className="h-3 w-3" />
                                    )}
                                    {t(
                                      'vault.management.resendWithNewEmail',
                                      'Envoyer à cette adresse'
                                    )}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : isSelected ? (
                          <div className="mt-2">
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-neutral-400" />
                              <input
                                type="email"
                                value={selectedData?.email || ''}
                                onChange={e =>
                                  handleEmailChange(
                                    employee.uuid,
                                    e.target.value
                                  )
                                }
                                placeholder={t(
                                  'vault.management.enterEmail',
                                  "Entrez l'email..."
                                )}
                                className="flex-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 focus:outline-none"
                              />
                            </div>
                            {!selectedData?.email.trim() && (
                              <p className="mt-1 flex items-center gap-1 text-xs text-amber-600">
                                <AlertCircle className="h-3 w-3" />
                                {t(
                                  'vault.management.emailRequired',
                                  "Email requis pour l'activation"
                                )}
                              </p>
                            )}
                          </div>
                        ) : (
                          employee.email && (
                            <p className="mt-1 text-sm text-neutral-500">
                              <Mail className="mr-1 inline h-3.5 w-3.5" />
                              {employee.email}
                            </p>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Action Button */}
        {selectedCount > 0 && (
          <div className="space-y-3">
            {!folderVault && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm text-amber-800">
                  <AlertCircle className="mr-1.5 inline h-4 w-4" />
                  {t(
                    'vault.management.folderVaultWillBeCreated',
                    'Le coffre-fort du dossier sera également activé automatiquement.'
                  )}
                </p>
              </div>
            )}

            <Button
              onClick={handleActivateEmployeeVaults}
              disabled={
                validSelectedCount === 0 ||
                massCreateMutation.isPending ||
                isActivatingFolder
              }
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              {massCreateMutation.isPending || isActivatingFolder ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('vault.management.activating', 'Activation...')}
                </>
              ) : (
                <>
                  <Shield className="mr-2 h-4 w-4" />
                  {t(
                    'vault.management.activateSelected',
                    'Activer les Coffres-Fort Sélectionnés'
                  )}{' '}
                  ({validSelectedCount})
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Upload Document Modal */}
      {uploadTarget && (
        <UploadDocumentModal
          isOpen={!!uploadTarget}
          onClose={() => setUploadTarget(null)}
          folderUuid={folderUuid}
          employeeUuid={uploadTarget.uuid}
          employeeName={uploadTarget.name}
          employeeMatricule={uploadTarget.matricule}
        />
      )}
    </div>
  );
}
